import React from 'react';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { Grid } from '@mui/material';

import { useSettings } from 'app/main/components/hooks';
import { SearchSelectField, SimpleTextField } from 'app/main/components/fields';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

export default function CompaniesDetailsSectionAddress(props: DetailsSectionInfoContentProps) {
	const { t } = useSettings();

	const isProspectOrCustomer = [1, 2].includes(props.model.comp_core_id);

	return (
		<>
			{/* ADDRESS */}
			<Grid size={12}>
				<SimpleTextField
					name="comp_address"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS')}
					value={props.model.comp_address}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ADDRESS CITY */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name="comp_city"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_CITY')}
					value={props.model.comp_city}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ADDRESS ZIP */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name="comp_zip"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_ZIP')}
					value={props.model.comp_zip}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ADDRESS DISTRICT */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="comp_dist_id"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_DISTRICT')}
					systable="districts"
					value={props.model.comp_dist_id}
					filters={
						props.model.comp_regi_id
							? [
									{
										filt_field: 'dist_regi_id',
										filt_clause: 'equals',
										filt_value: props.model.comp_regi_id
									}
								]
							: []
					}
					filterOptions={{ stringify: option => `${option.dist_iso} ${option.dist_title}` }}
					getOptionLabel={option => `${option.dist_iso} - ${option.dist_title}`}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					afterChange={value => {
						if (!value) return;
						props.sendChange('comp_ctry_id', value.regi_ctry_id);
						props.sendChange('comp_regi_id', value.dist_regi_id);
					}}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ADDRESS REGION */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="comp_regi_id"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_REGION')}
					systable="regions"
					value={props.model.comp_regi_id}
					filters={
						props.model.comp_ctry_id
							? [
									{
										filt_field: 'regi_ctry_id',
										filt_clause: 'equals',
										filt_value: props.model.comp_ctry_id
									}
								]
							: []
					}
					groupBy={option => option.ctry_title}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					afterChange={value => {
						if (!value) return;
						props.sendChange('comp_ctry_id', value.regi_ctry_id);
					}}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ADDRESS COUNTRY */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="comp_ctry_id"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_COUNTRY')}
					systable="countries"
					value={props.model.comp_ctry_id}
					filterOptions={{ stringify: option => `${option.ctry_title} ${option.ctry_iso}` }}
					getOptionLabel={option => `${option.ctry_iso} - ${option.ctry_title}`}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required={isProspectOrCustomer}
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* ADDRESS ZONE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="comp_zone_id"
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_ZONE')}
					systable="zones"
					value={props.model.comp_zone_id}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ADDRESS MAP */}
			{props.model.comp_lat && props.model.comp_lng && !props.isEdit && (
				<Grid size={12} minHeight={300}>
					<MapContainer
						center={[props.model.comp_lat, props.model.comp_lng]}
						zoom={13}
						className="z-0 h-full w-full"
						scrollWheelZoom={false}
					>
						<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
						<Marker position={[props.model.comp_lat, props.model.comp_lng]} />
					</MapContainer>
				</Grid>
			)}
		</>
	);
}
